import React, { useState, useEffect, useCallback } from 'react';
import {
    getBlogPosts,
    createBlogPost,
    updateBlogPost,
    deleteBlogPost
} from '../api';
import Pagination from './Pagination';
import logger from '../logger';
import './BlogManager.css';

const BlogManager = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const perPage = 10;

    // Filters
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    // Editor modal
    const [editorModal, setEditorModal] = useState({
        open: false,
        mode: 'create', // 'create' or 'edit'
        post: null
    });
    const [formData, setFormData] = useState({
        title: '',
        slug: '',
        content: '',
        excerpt: '',
        blog_category_id: '',
        file: '',
        is_published: false,
        published_at: '',
    });
    const [saving, setSaving] = useState(false);

    // Delete confirmation
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, postId: null, postTitle: '' });
    const [deleting, setDeleting] = useState(false);

    const fetchPosts = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await getBlogPosts(currentPage, perPage, statusFilter === 'all' ? '' : statusFilter);
            setPosts(result.data);
            setTotalPages(result.meta.totalPages);
            setTotal(result.meta.total);
        } catch (err) {
            logger.error('Failed to fetch blog posts:', err);
            setError('Failed to load blog posts. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [currentPage, statusFilter]);

    useEffect(() => {
        fetchPosts();
    }, [fetchPosts]);

    useEffect(() => {
        setCurrentPage(1);
    }, [statusFilter]);

    const generateSlug = (title) => {
        return title
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');
    };

    const formatDateTimeLocal = (dateStr) => {
        if (!dateStr) return '';
        const parsedDate = new Date(dateStr);
        if (Number.isNaN(parsedDate.getTime())) return '';
        return new Date(parsedDate.getTime() - parsedDate.getTimezoneOffset() * 60000)
            .toISOString()
            .slice(0, 16);
    };

    const handleTitleChange = (value) => {
        setFormData(prev => ({
            ...prev,
            title: value,
            slug: editorModal.mode === 'create' ? generateSlug(value) : prev.slug
        }));
    };

    const openCreateModal = () => {
        setFormData({
            title: '',
            slug: '',
            content: '',
            excerpt: '',
            blog_category_id: '',
            file: '',
            is_published: false,
            published_at: '',
        });
        setEditorModal({ open: true, mode: 'create', post: null });
    };

    const openEditModal = async (post) => {
        setFormData({
            title: post.title || '',
            slug: post.slug || '',
            content: post.content || '',
            excerpt: post.excerpt || '',
            blog_category_id: post.blog_category_id ? String(post.blog_category_id) : '',
            file: post.file || '',
            is_published: post.is_published === true || post.is_published === 1 || post.is_published === '1',
            published_at: formatDateTimeLocal(post.published_at),
        });
        setEditorModal({ open: true, mode: 'edit', post });
    };

    const closeEditorModal = () => {
        setEditorModal({ open: false, mode: 'create', post: null });
        setFormData({
            title: '',
            slug: '',
            content: '',
            excerpt: '',
            blog_category_id: '',
            file: '',
            is_published: false,
            published_at: '',
        });
    };

    const handleSave = async () => {
        if (!formData.title.trim()) {
            setError('Title is required');
            return;
        }

        setSaving(true);
        setError(null);
        try {
            const data = {
                title: formData.title.trim(),
                slug: formData.slug.trim() || generateSlug(formData.title),
                content: formData.content,
                excerpt: formData.excerpt.trim(),
                blog_category_id: formData.blog_category_id ? Number(formData.blog_category_id) : null,
                file: formData.file?.trim() || '',
                is_published: formData.is_published,
                published_at: formData.published_at ? new Date(formData.published_at).toISOString() : null,
            };

            if (editorModal.mode === 'create') {
                logger.info('Submitting create blog post request to /blogs', {
                    title: data.title,
                    slug: data.slug,
                    blog_category_id: data.blog_category_id,
                    is_published: data.is_published,
                    hasFile: Boolean(data.file),
                    published_at: data.published_at,
                });
                await createBlogPost(data);
                setSuccessMessage('Blog post created successfully.');
            } else {
                await updateBlogPost(editorModal.post.id, data);
                setSuccessMessage('Blog post updated successfully.');
            }

            setTimeout(() => setSuccessMessage(''), 3000);
            closeEditorModal();
            fetchPosts();
        } catch (err) {
            logger.error('Failed to save blog post:', err);
            setError(err.message || 'Failed to save blog post. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const openDeleteConfirm = (post) => {
        setDeleteConfirm({ open: true, postId: post.id, postTitle: post.title });
    };

    const closeDeleteConfirm = () => {
        setDeleteConfirm({ open: false, postId: null, postTitle: '' });
    };

    const handleDelete = async () => {
        if (!deleteConfirm.postId) return;

        setDeleting(true);
        try {
            await deleteBlogPost(deleteConfirm.postId);
            setSuccessMessage('Blog post deleted successfully.');
            setTimeout(() => setSuccessMessage(''), 3000);
            closeDeleteConfirm();
            fetchPosts();
        } catch (err) {
            logger.error('Failed to delete blog post:', err);
            setError('Failed to delete blog post. Please try again.');
        } finally {
            setDeleting(false);
        }
    };

    const filteredPosts = posts.filter(post => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return post.title?.toLowerCase().includes(search) ||
            post.excerpt?.toLowerCase().includes(search);
    });

    const getStatusBadgeClass = (status) => {
        return status ? 'status-published' : 'status-draft';
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    return (
        <div className="blog-manager-page">
            <div className="blog-header">
                <div className="header-content">
                    <h1>Blog Manager</h1>
                    <p>Create and manage blog posts for learners</p>
                </div>
                <button className="btn-create-post" onClick={openCreateModal}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    New Post
                </button>
            </div>

            {error && (
                <div className="error-banner">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                    </svg>
                    {error}
                    <button onClick={() => setError(null)}>×</button>
                </div>
            )}

            {successMessage && (
                <div className="success-banner">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                    </svg>
                    {successMessage}
                </div>
            )}

            <div className="blog-toolbar">
                <div className="filter-tabs">
                    {['all', 'published', 'draft'].map(status => (
                        <button
                            key={status}
                            className={`filter-tab ${statusFilter === status ? 'active' : ''}`}
                            onClick={() => setStatusFilter(status)}
                        >
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                        </button>
                    ))}
                </div>

                <div className="search-box">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" />
                        <path d="m21 21-4.35-4.35" />
                    </svg>
                    <input
                        type="text"
                        placeholder="Search posts..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            <div className="blog-content">
                {loading ? (
                    <div className="loading-state">
                        <div className="spinner"></div>
                        <p>Loading posts...</p>
                    </div>
                ) : filteredPosts.length === 0 ? (
                    <div className="empty-state">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M12 20h9" />
                            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                        </svg>
                        <h3>No posts found</h3>
                        <p>
                            {searchTerm
                                ? 'Try adjusting your search terms.'
                                : 'Create your first blog post to get started.'}
                        </p>
                        {!searchTerm && (
                            <button className="btn-create-post" onClick={openCreateModal}>
                                Create Post
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="posts-grid">
                            {filteredPosts.map(post => (
                                <div key={post.id} className="post-card">
                                    <div className="post-content">
                                        <div className="post-meta">
                                            <span className={`status-badge ${getStatusBadgeClass(post.is_published)}`}>
                                                {post.is_published ? 'Published' : 'Draft'}
                                            </span>
                                            <span className="post-date">{formatDate(post.published_at || post.created_at)}</span>
                                        </div>
                                        <h3>{post.title || 'Untitled'}</h3>
                                        <p className="post-excerpt">
                                            {post.excerpt || post.content?.substring(0, 100) + '...' || 'No content'}
                                        </p>
                                        <div className="post-actions">
                                            <button className="btn-edit" onClick={() => openEditModal(post)}>
                                                Edit
                                            </button>
                                            <button className="btn-delete" onClick={() => openDeleteConfirm(post)}>
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                            isLoading={loading}
                        />
                    </>
                )}
            </div>

            {/* Editor Modal */}
            {editorModal.open && (
                <div className="modal-overlay" onClick={closeEditorModal}>
                    <div className="editor-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editorModal.mode === 'create' ? 'Create New Post' : 'Edit Post'}</h2>
                            <button className="close-btn" onClick={closeEditorModal}>×</button>
                        </div>

                        <div className="modal-body">
                            <div className="form-row">
                                <div className="form-group full-width">
                                    <label>Title *</label>
                                    <input
                                        type="text"
                                        value={formData.title}
                                        onChange={e => handleTitleChange(e.target.value)}
                                        placeholder="Enter post title..."
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Slug</label>
                                    <input
                                        type="text"
                                        value={formData.slug}
                                        onChange={e => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                                        placeholder="url-friendly-slug"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Status</label>
                                    <select
                                        value={formData.is_published ? '1' : '0'}
                                        onChange={e => setFormData(prev => ({ ...prev, is_published: e.target.value === '1' }))}
                                    >
                                        <option value="0">Draft</option>
                                        <option value="1">Published</option>
                                    </select>
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Blog Category ID</label>
                                    <input
                                        type="number"
                                        value={formData.blog_category_id}
                                        onChange={e => setFormData(prev => ({ ...prev, blog_category_id: e.target.value }))}
                                        placeholder="e.g., 1"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>File</label>
                                    <input
                                        type="text"
                                        value={formData.file}
                                        onChange={e => setFormData(prev => ({ ...prev, file: e.target.value }))}
                                        placeholder="https://example.com/file.jpg"
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group full-width">
                                    <label>Published At</label>
                                    <input
                                        type="datetime-local"
                                        value={formData.published_at}
                                        onChange={e => setFormData(prev => ({ ...prev, published_at: e.target.value }))}
                                    />
                                </div>
                            </div>

                            <div className="form-group full-width">
                                <label>Excerpt</label>
                                <textarea
                                    value={formData.excerpt}
                                    onChange={e => setFormData(prev => ({ ...prev, excerpt: e.target.value }))}
                                    placeholder="Brief summary of the post..."
                                    rows={2}
                                />
                            </div>

                            <div className="form-group full-width">
                                <label>Content</label>
                                <textarea
                                    value={formData.content}
                                    onChange={e => setFormData(prev => ({ ...prev, content: e.target.value }))}
                                    placeholder="Write your post content here... (supports HTML)"
                                    rows={12}
                                    className="content-editor"
                                />
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={closeEditorModal}>
                                Cancel
                            </button>
                            <button
                                className="btn-save"
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? 'Saving...' : (editorModal.mode === 'create' ? 'Create Post' : 'Save Changes')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirm.open && (
                <div className="modal-overlay" onClick={closeDeleteConfirm}>
                    <div className="confirm-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Delete Post</h2>
                            <button className="close-btn" onClick={closeDeleteConfirm}>×</button>
                        </div>
                        <div className="modal-body">
                            <p>Are you sure you want to delete <strong>"{deleteConfirm.postTitle}"</strong>?</p>
                            <p className="warning-text">This action cannot be undone.</p>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={closeDeleteConfirm}>
                                Cancel
                            </button>
                            <button
                                className="btn-delete"
                                onClick={handleDelete}
                                disabled={deleting}
                            >
                                {deleting ? 'Deleting...' : 'Delete Post'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BlogManager;
